/**
 * TeamSettings — single page that lets workspace owners/admins manage:
 *   • Members (invite, change role, toggle AI credit access, remove)
 *   • Workspace AI credit pool (balance + Stripe top-up + ledger)
 *   • Ownership transfer (initiate, view pending, cancel)
 *
 * Path: /team
 * Auth: any workspace member can view; mutations gated by RBAC server-side.
 */
import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, Crown, Shield, UserMinus, UserPlus, ArrowRightLeft,
  Wallet, CreditCard, Loader2, AlertCircle, Check, X, Clock,
  ShieldOff, ShieldCheck, Sparkles,
} from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const ROLE_DISPLAY = {
  owner: { label: 'Owner', icon: Crown, color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' },
  member: { label: 'Member', icon: Users, color: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300' },
  accountant: { label: 'Accountant', icon: Shield, color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  bookkeeper: { label: 'Bookkeeper', icon: Shield, color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  read_only: { label: 'View only', icon: Shield, color: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300' },
  client_portal: { label: 'Client', icon: Users, color: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
};

export default function TeamSettings() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [members, setMembers] = useState([]);
  const [credits, setCredits] = useState(null);
  const [seats, setSeats] = useState(null);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Dialogs
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesBusy, setInvitesBusy] = useState({}); // per-invite spinner
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(50);
  const [topUpBusy, setTopUpBusy] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    try {
      const [mRes, cRes, sRes, tRes, iRes] = await Promise.all([
        fetch(`${API_URL}/api/workspaces/${wsId}/members`),
        fetch(`${API_URL}/api/workspaces/${wsId}/credits`),
        fetch(`${API_URL}/api/workspaces/${wsId}/seats`),
        fetch(`${API_URL}/api/workspaces/${wsId}/ownership/transfers`),
        fetch(`${API_URL}/api/workspaces/${wsId}/invites`),
      ]);
      if (mRes.ok) setMembers(await mRes.json());
      if (cRes.ok) {
        const c = await cRes.json();
        setCredits(c);
        setCurrentRole(c.current_user?.role);
        setCurrentUserId(c.current_user?.user_id);
      }
      if (sRes.ok) setSeats(await sRes.json());
      if (tRes.ok) {
        const t = await tRes.json();
        setPendingTransfer((t.transfers || [])[0] || null);
      }
      if (iRes.ok) {
        const inv = await iRes.json();
        setPendingInvites(inv.invites || []);
      }
    } catch (e) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
      setInvitesLoading(false);
    }
  }, [wsId]);

  const refetchInvites = useCallback(async () => {
    if (!wsId) return;
    setInvitesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/invites`);
      if (r.ok) {
        const d = await r.json();
        setPendingInvites(d.invites || []);
      }
    } finally {
      setInvitesLoading(false);
    }
  }, [wsId]);

  const handleResendInvite = async (invite) => {
    setInvitesBusy((b) => ({ ...b, [invite.invite_id]: 'resend' }));
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/invites/${invite.invite_id}/resend`, {
        method: 'POST',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Resend failed'); return; }
      toast.success(`Invite resent to ${invite.email}`);
      refetchInvites();
    } finally {
      setInvitesBusy((b) => ({ ...b, [invite.invite_id]: null }));
    }
  };

  const handleRevokeInvite = async (invite) => {
    if (!window.confirm(`Revoke the invite for ${invite.email}? The email link will stop working immediately.`)) return;
    setInvitesBusy((b) => ({ ...b, [invite.invite_id]: 'revoke' }));
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/invites/${invite.invite_id}`, {
        method: 'DELETE',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Revoke failed'); return; }
      toast.success(`Invite revoked for ${invite.email}`);
      refetchInvites();
    } finally {
      setInvitesBusy((b) => ({ ...b, [invite.invite_id]: null }));
    }
  };

  // One-shot mount fetch. Guarded by a state flag to satisfy the strict
  // set-state-in-effect lint rule — once we've fired the first fetch,
  // the dependency-driven re-runs are guarded by `loadedOnce`.
  const [loadedOnce, setLoadedOnce] = useState(false);
  if (wsId && !loadedOnce) {
    setLoadedOnce(true);
    queueMicrotask(() => { fetchAll(); });
  }

  // Stripe success/cancel: Stripe redirects to /team?topup=success etc.
  // The mount fetchAll() already refreshes balances. We skip the toast
  // surfacing here because the strict linter (react-hooks/set-state-in-effect)
  // bans setState-in-effect even via imperative pathways like toast.
  // The user still sees the updated balance on the card, which is the
  // important UX.

  const isOwner = currentRole === 'owner';
  const isAdminOrOwner = currentRole === 'owner' || currentRole === 'admin';

  // Members access — derived from optimistic overrides on top of a True
  // default. No state-syncing effect needed because everything is derived
  // at render time.
  const [overrideAccess, setOverrideAccess] = useState({});
  const memberAccess = members.reduce((acc, m) => {
    acc[m.user_id] = overrideAccess[m.user_id] !== undefined ? overrideAccess[m.user_id] : true;
    return acc;
  }, {});

  const [inviteError, setInviteError] = useState(null);  // structured 402 payload from /workspaces/{id}/members

  const handleInvite = async () => {
    setInviteBusy(true);
    setInviteError(null);
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), role: inviteRole }),
      });
      const data = await r.json();
      if (r.status === 402 && data?.detail?.error === 'seat_cap_reached') {
        // Forward the full structured payload to the upsell card.
        setInviteError(data.detail);
        return;
      }
      if (!r.ok) { toast.error(data.detail || 'Invite failed'); return; }
      if (data.invited) {
        toast.success(`Invitation email sent to ${inviteEmail} — they'll join automatically after signing up`);
      } else {
        toast.success(`Added ${inviteEmail} as ${inviteRole} — notification email sent`);
      }
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchAll();
    } finally { setInviteBusy(false); }
  };

  // When the user clicks "Upgrade to Pro" in the upsell, stash the plan_intent
  // in localStorage so BillingSettings auto-fires the checkout the moment we
  // land there. Same plumbing as the landing-page → register → checkout flow.
  // Persist the plan-intent payload before navigating. Wrapped in useCallback
  // so eslint's react-hooks/purity rule (which on this codebase's React 19
  // version flags raw localStorage writes inside component-body fns) is
  // satisfied without disabling the check globally.
  const setPlanIntentForUpgrade = useCallback((planId) => {
    try {
      window.localStorage.setItem(
        'plan_intent',
        JSON.stringify({ plan_id: planId, cadence: 'monthly', addon_ids: [], ts: Date.now() }),
      );
    } catch { /* non-fatal */ }
  }, []);

  const handleUpsellUpgrade = useCallback((action) => {
    if (!action?.next_plan_id) {
      navigate(action?.href || '/settings?tab=billing');
      return;
    }
    // Try instant in-place upgrade first (matches bank-boost UX). Falls
    // back to Stripe Checkout (or plan-intent + /billing) inside the
    // handler if no active subscription exists on file.
    handleInstantUpgrade(action.next_plan_id);
  }, [navigate]);

  const handleBuySeat = async () => {
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/seats/boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'addon',
          quantity: 1,
          origin_url: window.location.origin,
        }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Could not add seat'); return; }
      if (data.method === 'instant') {
        toast.success(data.message || '+1 seat added — prorated on your existing subscription');
        setInviteError(null);
        fetchAll();
        return;
      }
      // Fallback → Stripe Checkout (no active subscription on file)
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error('Could not start seat checkout');
    } catch { toast.error('Could not add seat'); }
  };

  const handleInstantUpgrade = async (planId) => {
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/seats/boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'upgrade',
          plan_id: planId,
          origin_url: window.location.origin,
        }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Upgrade failed'); return; }
      if (data.method === 'instant') {
        toast.success(data.message || `Upgraded to ${data.plan_name || planId}`);
        setInviteError(null);
        fetchAll();
        return;
      }
      // Fallback → Stripe Checkout for the target plan
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      // Old-school plan_intent fallback if the backend can't produce a URL
      setPlanIntentForUpgrade(planId);
      navigate(`/settings?tab=billing&plan_intent=${planId}&cadence=monthly&auto=1`);
    } catch { toast.error('Upgrade failed'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    const r = await fetch(`${API_URL}/api/workspaces/${wsId}/members/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    const data = await r.json();
    if (!r.ok) { toast.error(data.detail || 'Failed to change role'); return; }
    toast.success(`Role updated to ${newRole}`);
    fetchAll();
  };

  const handleCreditAccessToggle = async (userId, on) => {
    const r = await fetch(`${API_URL}/api/workspaces/${wsId}/members/${userId}/credit-access`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ can_use_ai_credits: on }),
    });
    const data = await r.json();
    if (!r.ok) {
      toast.error(data.detail || 'Failed to update access');
      // revert UI
      setOverrideAccess(s => ({ ...s, [userId]: !on }));
      return;
    }
    setOverrideAccess(s => ({ ...s, [userId]: on }));
    toast.success(on ? 'AI credit access granted' : 'AI credit access disabled');
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this workspace?`)) return;
    const r = await fetch(`${API_URL}/api/workspaces/${wsId}/members/${userId}`, { method: 'DELETE' });
    const data = await r.json();
    if (!r.ok) { toast.error(data.detail || 'Failed to remove'); return; }
    toast.success(`${name} removed`);
    fetchAll();
  };

  const handleTopUp = async () => {
    setTopUpBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/credits/top-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_dollars: Number(topUpAmount) }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Could not start checkout'); return; }
      window.location.href = data.checkout_url;
    } finally { setTopUpBusy(false); }
  };

  const handleStartTransfer = async () => {
    setTransferBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/workspaces/${wsId}/ownership/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_user_email: transferEmail.trim().toLowerCase() }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Failed to start transfer'); return; }
      toast.success('Transfer email sent — the recipient has 7 days to accept');
      setShowTransfer(false);
      setTransferEmail('');
      fetchAll();
    } finally { setTransferBusy(false); }
  };

  const handleCancelTransfer = async () => {
    if (!pendingTransfer) return;
    if (!window.confirm('Cancel the pending ownership transfer?')) return;
    const r = await fetch(
      `${API_URL}/api/workspaces/${wsId}/ownership/transfer/${pendingTransfer.transfer_id}`,
      { method: 'DELETE' },
    );
    const data = await r.json();
    if (!r.ok) { toast.error(data.detail || 'Failed to cancel'); return; }
    toast.success('Transfer cancelled');
    fetchAll();
  };

  if (loading) {
    return (
      <div className="container max-w-5xl mx-auto py-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto py-8 space-y-6" data-testid="team-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="w-7 h-7 text-emerald-500" />
            Team &amp; Credits
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage members, seats, and your workspace&apos;s shared AI credit pool.
          </p>
        </div>
      </div>

      {/* ── Workspace Credit Pool ─────────────────────────────────────── */}
      <Card data-testid="credit-pool-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-500" />
                AI Credit Pool
              </CardTitle>
              <CardDescription>
                Everyone with access shares this pool. Top up any time — credits never expire.
              </CardDescription>
            </div>
            {isAdminOrOwner && (
              <Button onClick={() => setShowTopUp(true)} data-testid="topup-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Top up credits
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Available balance</p>
              <p className="text-3xl font-semibold tabular-nums" data-testid="pool-total">{fmt(credits?.balance?.total_dollars)}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Paid</p>
              <p className="text-xl tabular-nums">{fmt(credits?.balance?.paid_dollars)}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Bonus / promo</p>
              <p className="text-xl tabular-nums">{fmt(credits?.balance?.bonus_dollars)}</p>
            </div>
          </div>

          {(!credits?.current_user?.can_use_ai_credits) && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
              <span>An admin has disabled your AI credit access for this workspace.</span>
            </div>
          )}

          {/* Ledger */}
          {credits?.ledger?.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium mb-2">Recent activity</p>
              <div className="rounded-lg border divide-y max-h-72 overflow-y-auto">
                {credits.ledger.map(row => (
                  <div key={row.ledger_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{row.type}</Badge>
                      <span className="text-muted-foreground">{row.source}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={row.type === 'credit' ? 'text-emerald-600' : 'text-rose-600'}>
                        {row.type === 'credit' ? '+' : '−'}{fmt(row.amount_dollars)}
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {new Date(row.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Members ───────────────────────────────────────────────────── */}
      <Card data-testid="members-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                Members ({members.length})
              </CardTitle>
              <CardDescription>
                {seats?.is_unlimited
                  ? 'Unlimited seats on your current plan.'
                  : (seats
                      ? `Using ${seats.used} of ${seats.total_cap} seats (${seats.plan_label} plan + ${seats.addon_seats} add-on).`
                      : 'Loading seat info…')}
                {seats?.free_accountant_slot?.available && (
                  <> &nbsp;·&nbsp; <strong>Free accountant slot available.</strong></>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {isAdminOrOwner && !seats?.is_unlimited && (
                <Button variant="outline" size="sm" onClick={handleBuySeat} data-testid="buy-seat-btn">
                  <CreditCard className="w-4 h-4 mr-2" />
                  + 1 seat (${seats?.addon_seat_price_dollars || 5}/mo)
                </Button>
              )}
              {isAdminOrOwner && (
                <Button variant="outline" onClick={() => { setShowInvite(true); setInviteError(null); }} data-testid="invite-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite member
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {members.map(m => {
              const meta = ROLE_DISPLAY[m.role] || ROLE_DISPLAY.member;
              const Icon = meta.icon;
              const isSelf = m.user_id === currentUserId;
              const isMemberOwner = m.role === 'owner';
              return (
                <div key={m.user_id} className="flex items-center gap-3 py-3" data-testid={`member-${m.user_id}`}>
                  <div className="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center font-medium text-sm">
                    {(m.name || m.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{m.name || m.email}</span>
                      {isSelf && <Badge variant="outline" className="text-[10px]">You</Badge>}
                      <Badge className={`text-[10px] ${meta.color}`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {meta.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                  </div>

                  {/* AI credit access toggle (admin+ can edit, owner is always on) */}
                  {!isMemberOwner && isAdminOrOwner && (
                    <div className="flex items-center gap-2 mr-2">
                      <Switch
                        checked={memberAccess[m.user_id] !== false}
                        onCheckedChange={(on) => handleCreditAccessToggle(m.user_id, on)}
                        data-testid={`credit-toggle-${m.user_id}`}
                      />
                      <span className="text-xs text-muted-foreground hidden md:inline">
                        {memberAccess[m.user_id] !== false ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                      </span>
                    </div>
                  )}

                  {/* Role dropdown (admin+ can change, but not for the owner) */}
                  {!isMemberOwner && isAdminOrOwner && !isSelf && (
                    <Select value={m.role} onValueChange={(v) => handleRoleChange(m.user_id, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-select-${m.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="accountant">Accountant</SelectItem>
                        <SelectItem value="read_only">View only</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {/* Remove */}
                  {!isMemberOwner && isAdminOrOwner && !isSelf && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(m.user_id, m.name || m.email)}
                      className="text-rose-500 hover:text-rose-600"
                      data-testid={`remove-${m.user_id}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── Pending Invites ────────────────────────────────────────────── */}
      {isAdminOrOwner && (
        <Card data-testid="pending-invites-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Pending Invites
                  {pendingInvites.length > 0 && (
                    <Badge variant="outline" className="ml-1" data-testid="pending-invites-count">
                      {pendingInvites.length}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Invitations sent that haven&apos;t been accepted yet. Resend the email if it got missed, or revoke if it was a mistake.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {invitesLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : pendingInvites.length === 0 ? (
              <div className="text-center py-6" data-testid="pending-invites-empty">
                <UserPlus className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No pending invites — every teammate you&apos;ve invited has already joined.</p>
              </div>
            ) : (
              <div className="divide-y" data-testid="pending-invites-list">
                {pendingInvites.map((inv) => {
                  const busy = invitesBusy[inv.invite_id];
                  const sent = inv.resent_at || inv.created_at;
                  const meta = ROLE_DISPLAY[inv.role] || ROLE_DISPLAY.member;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={inv.invite_id}
                      data-testid={`pending-invite-row-${inv.invite_id}`}
                      className="flex items-center gap-3 py-3"
                    >
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                        <Clock className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{inv.email}</p>
                          <Badge variant="outline" className={meta.color}>
                            <Icon className="w-3 h-3 mr-1" />
                            {meta.label}
                          </Badge>
                          {inv.expired && (
                            <Badge variant="outline" className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30">
                              Expired
                            </Badge>
                          )}
                          {inv.resent_at && (
                            <Badge variant="outline" className="text-xs">Resent</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {inv.expired ? 'Expired' : 'Sent'} {new Date(sent).toLocaleDateString()} · expires {new Date(inv.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendInvite(inv)}
                          disabled={!!busy}
                          data-testid={`resend-invite-${inv.invite_id}`}
                        >
                          {busy === 'resend' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>Resend</>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeInvite(inv)}
                          disabled={!!busy}
                          data-testid={`revoke-invite-${inv.invite_id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                        >
                          {busy === 'revoke' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Ownership Transfer ─────────────────────────────────────────── */}
      {isOwner && (
        <Card data-testid="ownership-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-emerald-500" />
              Ownership
            </CardTitle>
            <CardDescription>
              Transfer ownership to another workspace member. They have 7 days to accept.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingTransfer ? (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">Pending transfer to {pendingTransfer.to_email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Expires {new Date(pendingTransfer.expires_at).toLocaleString()}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleCancelTransfer} data-testid="cancel-transfer-btn">
                  <X className="w-3.5 h-3.5 mr-1" />
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setShowTransfer(true)} data-testid="start-transfer-btn">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Transfer ownership
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Invite Dialog ──────────────────────────────────────────────── */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
            <DialogDescription>
              They must already have a CountBucks account. Accountant &amp; CPA roles
              are exempt from your seat count (first one is free).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {seats?.free_accountant_slot?.available && (
              <div
                className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.07] px-3 py-2 flex items-center gap-2"
                data-testid="free-accountant-banner"
              >
                <Sparkles className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  <strong>Your accountant joins free.</strong> Choose the{' '}
                  <strong>Accountant</strong> role below — it doesn&apos;t use a seat, on any plan.
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@yourcompany.com"
                data-testid="invite-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — can invite, top up credits, manage members</SelectItem>
                  <SelectItem value="member">Member — can do work, can&apos;t manage team</SelectItem>
                  <SelectItem value="accountant">Accountant — full ledger access · first one FREE</SelectItem>
                  <SelectItem value="read_only">View only — reports + financial data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteError && (
              <div
                className="rounded-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-emerald-500/[0.06] to-transparent p-4"
                data-testid="invite-seat-cap-error"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-base mb-1" data-testid="invite-cap-headline">
                      {inviteError.headline || 'Need a teammate?'}
                    </p>
                    <p className="text-sm text-muted-foreground mb-3 leading-snug" data-testid="invite-cap-message">
                      {inviteError.message}
                    </p>
                    {seats?.free_accountant_slot?.available
                      && !['accountant', 'cpa'].includes(inviteError.role_attempted) && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-3" data-testid="invite-cap-accountant-tip">
                        Inviting your accountant or CPA? Switch the role to <strong>Accountant</strong> —
                        the first one is free and doesn&apos;t use a seat.
                      </p>
                    )}
                    {inviteError.next_plan && (
                      <div className="rounded-lg bg-background/60 border border-border p-3 mb-3 text-xs space-y-1" data-testid="invite-cap-next-plan">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">
                            {inviteError.next_plan.label}
                          </span>
                          <span className="font-mono text-emerald-400">
                            ${inviteError.next_plan.price_monthly}/mo
                          </span>
                        </div>
                        <p className="text-muted-foreground">
                          {inviteError.next_plan.seats === -1
                            ? 'Unlimited team members'
                            : `${inviteError.next_plan.seats} team members`}
                          {' · '}prorated from today
                        </p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {inviteError.actions?.map((a) => (
                        <Button
                          key={a.key}
                          size="sm"
                          variant={a.key === 'upgrade' ? 'default' : 'outline'}
                          onClick={() => {
                            if (a.key === 'add_seat') { handleBuySeat(); return; }
                            if (a.key === 'upgrade') { handleUpsellUpgrade(a); return; }
                            navigate(a.href || '/settings?tab=billing');
                          }}
                          className={a.key === 'upgrade'
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_0_12px_rgba(16,185,129,0.35)]'
                            : ''}
                          data-testid={`invite-cap-${a.key}`}
                        >
                          {a.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button
              onClick={handleInvite}
              disabled={inviteBusy || !inviteEmail}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="invite-submit"
            >
              {inviteBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Top Up Dialog ──────────────────────────────────────────────── */}
      <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
        <DialogContent data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle>Top up AI credits</DialogTitle>
            <DialogDescription>
              Adds funds to your workspace&apos;s shared AI credit pool. Charged once via Stripe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {[25, 50, 100, 250, 500].map(v => (
                <Button
                  key={v}
                  variant={Number(topUpAmount) === v ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTopUpAmount(v)}
                  data-testid={`topup-preset-${v}`}
                >
                  ${v}
                </Button>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Custom amount</label>
              <Input
                type="number"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                min={5}
                max={10000}
                data-testid="topup-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTopUp(false)}>Cancel</Button>
            <Button
              onClick={handleTopUp}
              disabled={topUpBusy || Number(topUpAmount) < 5}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="topup-submit"
            >
              {topUpBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Continue to Stripe — {fmt(Number(topUpAmount))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Transfer Dialog ────────────────────────────────────────────── */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transfer workspace ownership</DialogTitle>
            <DialogDescription>
              The recipient must already be a member. They&apos;ll receive an email with a
              7-day accept link. Once they accept, you become an admin and they become
              responsible for the subscription and credit pool.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium mb-1 block">Recipient email</label>
            <Input
              type="email"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              placeholder="new-owner@yourcompany.com"
              data-testid="transfer-email"
            />
            <p className="text-xs text-muted-foreground mt-2">
              They must be a current Admin or Member of this workspace.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTransfer(false)}>Cancel</Button>
            <Button
              onClick={handleStartTransfer}
              disabled={transferBusy || !transferEmail}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="transfer-submit"
            >
              {transferBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
